import React, { Component } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
const DowntimeBreakdownAnalysisDashboard: React.FC = () => {
  // KPI Data
  const kpis = [{
    title: 'Total Downtime',
    value: '287 Hrs',
    change: '-12.5%',
    isPositive: true,
    subtitle: 'This month'
  }, {
    title: 'MTTR',
    value: '2.4 Hrs',
    change: '-0.3 hrs',
    isPositive: true,
    subtitle: 'Mean Time To Repair'
  }, {
    title: 'MTBF',
    value: '156 Hrs',
    change: '+8 hrs',
    isPositive: true,
    subtitle: 'Mean Time Between Failures'
  }, {
    title: 'Breakdown Rate',
    value: '3.2%',
    change: '-0.5%',
    isPositive: true,
    subtitle: 'Of total operating time'
  }];
  // Downtime Pareto Chart Data (sorted by downtime hours)
  const downtimePareto = [{
    cause: 'Equipment Failure',
    hours: 85,
    frequency: 28,
    cumulativePercent: 29.6
  }, {
    cause: 'Planned Maintenance',
    hours: 62,
    frequency: 15,
    cumulativePercent: 51.2
  }, {
    cause: 'Material Shortage',
    hours: 48,
    frequency: 22,
    cumulativePercent: 67.9
  }, {
    cause: 'Tool Change',
    hours: 35,
    frequency: 45,
    cumulativePercent: 80.1
  }, {
    cause: 'Quality Issues',
    hours: 28,
    frequency: 18,
    cumulativePercent: 89.9
  }, {
    cause: 'Power Outage',
    hours: 18,
    frequency: 8,
    cumulativePercent: 96.2
  }, {
    cause: 'Operator Error',
    hours: 11,
    frequency: 12,
    cumulativePercent: 100
  }];
  // Breakdown Frequency Chart Data (by equipment type)
  const breakdownFrequencyData = [{
    equipment: 'CNC Machines',
    breakdowns: 42,
    avgDowntime: 3.2,
    severity: 'High'
  }, {
    equipment: 'Injection Molding',
    breakdowns: 35,
    avgDowntime: 4.1,
    severity: 'Critical'
  }, {
    equipment: 'Assembly Robots',
    breakdowns: 28,
    avgDowntime: 2.8,
    severity: 'Medium'
  }, {
    equipment: 'Conveyors',
    breakdowns: 24,
    avgDowntime: 1.5,
    severity: 'Low'
  }, {
    equipment: 'Testing Equipment',
    breakdowns: 22,
    avgDowntime: 2.2,
    severity: 'Medium'
  }, {
    equipment: 'Packaging',
    breakdowns: 18,
    avgDowntime: 1.8,
    severity: 'Low'
  }];
  // Downtime Trend Data (Last 12 Months)
  const downtimeTrendData = [{
    month: 'Jan',
    planned: 55,
    unplanned: 98,
    total: 153
  }, {
    month: 'Feb',
    planned: 58,
    unplanned: 92,
    total: 150
  }, {
    month: 'Mar',
    planned: 62,
    unplanned: 88,
    total: 150
  }, {
    month: 'Apr',
    planned: 60,
    unplanned: 85,
    total: 145
  }, {
    month: 'May',
    planned: 65,
    unplanned: 82,
    total: 147
  }, {
    month: 'Jun',
    planned: 63,
    unplanned: 78,
    total: 141
  }, {
    month: 'Jul',
    planned: 68,
    unplanned: 75,
    total: 143
  }, {
    month: 'Aug',
    planned: 70,
    unplanned: 72,
    total: 142
  }, {
    month: 'Sep',
    planned: 62,
    unplanned: 68,
    total: 130
  }, {
    month: 'Oct',
    planned: 64,
    unplanned: 65,
    total: 129
  }, {
    month: 'Nov',
    planned: 66,
    unplanned: 62,
    total: 128
  }, {
    month: 'Dec',
    planned: 62,
    unplanned: 58,
    total: 120
  }];
  // Line-wise Downtime Comparison
  const lineDowntimeData = [{
    line: 'Line 1',
    downtime: 52,
    mttr: 2.3,
    mtbf: 148
  }, {
    line: 'Line 2',
    downtime: 48,
    mttr: 2.1,
    mtbf: 162
  }, {
    line: 'Line 3',
    downtime: 68,
    mttr: 2.8,
    mtbf: 135
  }, {
    line: 'Line 4',
    downtime: 55,
    mttr: 2.4,
    mtbf: 152
  }, {
    line: 'Line 5',
    downtime: 64,
    mttr: 2.6,
    mtbf: 142
  }];
  // Equipment Downtime Log Table Data
  const equipmentDowntimeLog = [{
    date: '2024-01-15',
    time: '08:45',
    equipment: 'CNC-01',
    line: 'Line 1',
    downtime: 3.5,
    cause: 'Equipment Failure',
    description: 'Spindle motor overheating',
    mttr: 3.5,
    severity: 'High',
    status: 'Resolved',
    technician: 'Rajesh Kumar',
    cost: 45000
  }, {
    date: '2024-01-15',
    time: '14:20',
    equipment: 'Injection-02',
    line: 'Line 2',
    downtime: 5.2,
    cause: 'Equipment Failure',
    description: 'Hydraulic pump failure',
    mttr: 5.2,
    severity: 'Critical',
    status: 'Resolved',
    technician: 'Amit Sharma',
    cost: 85000
  }, {
    date: '2024-01-16',
    time: '10:15',
    equipment: 'Assembly-01',
    line: 'Line 3',
    downtime: 2.8,
    cause: 'Material Shortage',
    description: 'Component shortage from vendor',
    mttr: 2.8,
    severity: 'Medium',
    status: 'Resolved',
    technician: 'Priya Patel',
    cost: 0
  }, {
    date: '2024-01-16',
    time: '16:30',
    equipment: 'CNC-03',
    line: 'Line 1',
    downtime: 1.5,
    cause: 'Tool Change',
    description: 'Scheduled tool replacement',
    mttr: 1.5,
    severity: 'Low',
    status: 'Resolved',
    technician: 'Suresh Reddy',
    cost: 12000
  }, {
    date: '2024-01-17',
    time: '09:00',
    equipment: 'Testing-01',
    line: 'Line 4',
    downtime: 2.2,
    cause: 'Quality Issues',
    description: 'Calibration drift detected',
    mttr: 2.2,
    severity: 'Medium',
    status: 'Resolved',
    technician: 'Anil Verma',
    cost: 18000
  }, {
    date: '2024-01-17',
    time: '13:45',
    equipment: 'Injection-01',
    line: 'Line 2',
    downtime: 4.8,
    cause: 'Equipment Failure',
    description: 'Heating element malfunction',
    mttr: 4.8,
    severity: 'Critical',
    status: 'Resolved',
    technician: 'Vikram Singh',
    cost: 72000
  }, {
    date: '2024-01-18',
    time: '07:30',
    equipment: 'Conveyor-02',
    line: 'Line 3',
    downtime: 1.2,
    cause: 'Planned Maintenance',
    description: 'Routine belt inspection',
    mttr: 1.2,
    severity: 'Low',
    status: 'Resolved',
    technician: 'Ramesh Gupta',
    cost: 5000
  }, {
    date: '2024-01-18',
    time: '15:00',
    equipment: 'CNC-02',
    line: 'Line 1',
    downtime: 3.2,
    cause: 'Equipment Failure',
    description: 'Control panel error',
    mttr: 3.2,
    severity: 'High',
    status: 'Resolved',
    technician: 'Deepak Joshi',
    cost: 38000
  }, {
    date: '2024-01-19',
    time: '11:20',
    equipment: 'Assembly-02',
    line: 'Line 4',
    downtime: 2.5,
    cause: 'Power Outage',
    description: 'Plant-wide power interruption',
    mttr: 2.5,
    severity: 'Medium',
    status: 'Resolved',
    technician: 'Electrical Team',
    cost: 0
  }, {
    date: '2024-01-19',
    time: '18:45',
    equipment: 'Packaging-01',
    line: 'Line 5',
    downtime: 1.8,
    cause: 'Operator Error',
    description: 'Incorrect parameter setting',
    mttr: 1.8,
    severity: 'Low',
    status: 'Resolved',
    technician: 'Sunil Mehta',
    cost: 0
  }, {
    date: '2024-01-20',
    time: '08:00',
    equipment: 'Injection-03',
    line: 'Line 2',
    downtime: 6.5,
    cause: 'Equipment Failure',
    description: 'Mold clamping system failure',
    mttr: 6.5,
    severity: 'Critical',
    status: 'Resolved',
    technician: 'Manoj Kumar',
    cost: 125000
  }, {
    date: '2024-01-20',
    time: '14:30',
    equipment: 'Testing-02',
    line: 'Line 3',
    downtime: 2.0,
    cause: 'Quality Issues',
    description: 'Sensor malfunction',
    mttr: 2.0,
    severity: 'Medium',
    status: 'Resolved',
    technician: 'Ravi Shankar',
    cost: 22000
  }, {
    date: '2024-01-21',
    time: '10:45',
    equipment: 'CNC-04',
    line: 'Line 4',
    downtime: 2.8,
    cause: 'Tool Change',
    description: 'End mill replacement',
    mttr: 2.8,
    severity: 'Low',
    status: 'Resolved',
    technician: 'Kiran Desai',
    cost: 15000
  }, {
    date: '2024-01-21',
    time: '16:15',
    equipment: 'Assembly-03',
    line: 'Line 5',
    downtime: 3.5,
    cause: 'Material Shortage',
    description: 'Fastener stock depletion',
    mttr: 3.5,
    severity: 'Medium',
    status: 'Resolved',
    technician: 'Store Team',
    cost: 0
  }, {
    date: '2024-01-22',
    time: '09:30',
    equipment: 'Conveyor-01',
    line: 'Line 1',
    downtime: 1.5,
    cause: 'Planned Maintenance',
    description: 'Lubrication service',
    mttr: 1.5,
    severity: 'Low',
    status: 'Resolved',
    technician: 'Maintenance Team',
    cost: 3000
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'bg-red-100 text-red-700';
      case 'High':
        return 'bg-orange-100 text-orange-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#6366f1'];
  return <div data-id="element-5054">
      <DescriptionPanel title="Downtime & Breakdown Analysis" description="Comprehensive analysis of equipment downtime, breakdown patterns, and maintenance metrics. Track MTTR, MTBF, and identify critical failure modes to improve equipment reliability and reduce unplanned downtime." insights={['Total downtime reduced by 12.5% to 287 hours this month', 'Equipment Failure is the top cause at 85 hours (29.6% of total downtime)', 'MTTR improved to 2.4 hours with MTBF at 156 hours', 'Injection molding equipment showing critical breakdown frequency requiring attention']} data-id="element-5055" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-5056">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5057">
          Downtime & Reliability Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-5058">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-5059">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-5060">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-2" data-id="element-5061">{kpi.subtitle}</div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5062">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-5063">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-5064" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-5065" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Downtime Pareto Chart */}
      <Card className="p-6 mb-8" data-id="element-5066">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5067">
          Downtime Pareto Analysis
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-5068">
          Top causes of downtime ranked by total hours - Focus on the vital few
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-5069">
          <ComposedChart data={downtimePareto} data-id="element-5070">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5071" />
            <XAxis dataKey="cause" angle={-45} textAnchor="end" height={120} tick={{
            fontSize: 11
          }} data-id="element-5072" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} label={{
            value: 'Downtime (Hours)',
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 12
            }
          }} data-id="element-5073" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{
            fontSize: 12
          }} label={{
            value: 'Cumulative %',
            angle: 90,
            position: 'insideRight',
            style: {
              fontSize: 12
            }
          }} data-id="element-5074" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-5075" />
            <Legend data-id="element-5076" />
            <Bar yAxisId="left" dataKey="hours" name="Downtime Hours" data-id="element-5077">
              {downtimePareto.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-5078" />)}
            </Bar>
            <Line yAxisId="right" type="monotone" dataKey="cumulativePercent" stroke="#1f2937" strokeWidth={3} name="Cumulative %" dot={{
            r: 5,
            fill: '#1f2937'
          }} data-id="element-5079" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5080">
          <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-5081">
            <div className="text-center" data-id="element-5082">
              <div className="text-gray-600" data-id="element-5083">Top Cause</div>
              <div className="text-lg font-bold text-red-600" data-id="element-5084">
                Equipment Failure
              </div>
              <div className="text-xs text-gray-500" data-id="element-5085">85 hours (29.6%)</div>
            </div>
            <div className="text-center" data-id="element-5086">
              <div className="text-gray-600" data-id="element-5087">80/20 Threshold</div>
              <div className="text-lg font-bold text-gray-900" data-id="element-5088">
                Top 4 Causes
              </div>
              <div className="text-xs text-gray-500" data-id="element-5089">80.1% of downtime</div>
            </div>
            <div className="text-center" data-id="element-5090">
              <div className="text-gray-600" data-id="element-5091">Total Incidents</div>
              <div className="text-lg font-bold text-gray-900" data-id="element-5092">148</div>
              <div className="text-xs text-gray-500" data-id="element-5093">this month</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Breakdown Frequency and Downtime Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-5094">
        {/* Breakdown Frequency Chart */}
        <Card className="p-6" data-id="element-5095">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5096">
            Breakdown Frequency by Equipment
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-5097">
            Number of breakdowns and average downtime per incident
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-5098">
            <ComposedChart data={breakdownFrequencyData} layout="vertical" data-id="element-5099">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5100" />
              <XAxis type="number" tick={{
              fontSize: 11
            }} data-id="element-5101" />
              <YAxis dataKey="equipment" type="category" width={120} tick={{
              fontSize: 11
            }} data-id="element-5102" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-5103" />
              <Legend data-id="element-5104" />
              <Bar dataKey="breakdowns" fill="#3b82f6" name="# of Breakdowns" data-id="element-5105" />
              <Line type="monotone" dataKey="avgDowntime" stroke="#ef4444" strokeWidth={2} name="Avg Downtime (hrs)" dot={{
              r: 4
            }} data-id="element-5106" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5107">
            <div className="space-y-2" data-id="element-5108">
              {breakdownFrequencyData.map((item, index) => <div key={index} className="flex justify-between items-center text-sm" data-id="element-5109">
                  <span className="text-gray-700 font-medium" data-id="element-5110">
                    {item.equipment}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getSeverityColor(item.severity)}`} data-id="element-5111">
                    {item.severity}
                  </span>
                </div>)}
            </div>
          </div>
        </Card>
        {/* Downtime Trend */}
        <Card className="p-6" data-id="element-5112">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5113">
            Downtime Trend (Last 12 Months)
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-5114">
            Planned vs Unplanned downtime over time
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-5115">
            <ComposedChart data={downtimeTrendData} data-id="element-5116">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5117" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-5118" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-5119" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-5120" />
              <Legend data-id="element-5121" />
              <Bar dataKey="planned" stackId="a" fill="#10b981" name="Planned" data-id="element-5122" />
              <Bar dataKey="unplanned" stackId="a" fill="#ef4444" name="Unplanned" data-id="element-5123" />
              <Line type="monotone" dataKey="total" stroke="#1f2937" strokeWidth={2} name="Total" dot={{
              r: 4
            }} data-id="element-5124" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5125">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-5126">
              <div className="text-center" data-id="element-5127">
                <div className="text-gray-600" data-id="element-5128">Planned</div>
                <div className="text-lg font-bold text-green-600" data-id="element-5129">62 hrs</div>
                <div className="text-xs text-gray-500" data-id="element-5130">this month</div>
              </div>
              <div className="text-center" data-id="element-5131">
                <div className="text-gray-600" data-id="element-5132">Unplanned</div>
                <div className="text-lg font-bold text-red-600" data-id="element-5133">58 hrs</div>
                <div className="text-xs text-gray-500" data-id="element-5134">this month</div>
              </div>
              <div className="text-center" data-id="element-5135">
                <div className="text-gray-600" data-id="element-5136">Trend</div>
                <div className="text-lg font-bold text-green-600" data-id="element-5137">-21.6%</div>
                <div className="text-xs text-gray-500" data-id="element-5138">YoY reduction</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Line-wise Downtime Comparison */}
      <Card className="p-6 mb-8" data-id="element-5139">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5140">
          Line-wise Downtime & Reliability Metrics
        </h3>
        <ResponsiveContainer width="100%" height={300} data-id="element-5141">
          <ComposedChart data={lineDowntimeData} data-id="element-5142">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5143" />
            <XAxis dataKey="line" tick={{
            fontSize: 12
          }} data-id="element-5144" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} data-id="element-5145" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 12
          }} data-id="element-5146" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-5147" />
            <Legend data-id="element-5148" />
            <Bar yAxisId="left" dataKey="downtime" fill="#ef4444" name="Downtime (hrs)" data-id="element-5149" />
            <Line yAxisId="right" type="monotone" dataKey="mttr" stroke="#f59e0b" strokeWidth={2} name="MTTR (hrs)" dot={{
            r: 4
          }} data-id="element-5150" />
            <Line yAxisId="right" type="monotone" dataKey="mtbf" stroke="#10b981" strokeWidth={2} name="MTBF (hrs)" dot={{
            r: 4
          }} data-id="element-5151" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5152">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-5153">
            {lineDowntimeData.map((line, index) => <div key={index} className="text-center" data-id="element-5154">
                <div className="text-gray-600" data-id="element-5155">{line.line}</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-5156">
                  {line.downtime} hrs
                </div>
                <div className="text-xs text-gray-500" data-id="element-5157">MTBF: {line.mtbf}h</div>
              </div>)}
          </div>
        </div>
      </Card>
      {/* Equipment Downtime Log Table */}
      <Card className="p-6" data-id="element-5158">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5159">
          Equipment Downtime Log
        </h3>
        <div className="overflow-x-auto" data-id="element-5160">
          <table className="w-full" data-id="element-5161">
            <thead data-id="element-5162">
              <tr className="border-b-2 border-gray-300" data-id="element-5163">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5164">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5165">
                  Time
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5166">
                  Equipment
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5167">
                  Line
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5168">
                  Downtime (hrs)
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5169">
                  Cause
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5170">
                  Description
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5171">
                  MTTR
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5172">
                  Severity
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5173">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5174">
                  Technician
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5175">
                  Cost (₹)
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5176">
              {equipmentDowntimeLog.map((log, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-5177">
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5178">
                    {new Date(log.date).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short'
                })}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5179">
                    {log.time}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-5180">
                    {log.equipment}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5181">
                    {log.line}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-5182">
                    {log.downtime.toFixed(1)}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5183">
                    {log.cause}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 max-w-xs truncate" data-id="element-5184">
                    {log.description}
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-5185">
                    {log.mttr.toFixed(1)}h
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5186">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getSeverityColor(log.severity)}`} data-id="element-5187">
                      {log.severity}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5188">
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-5189">
                      {log.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5190">
                    {log.technician}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-5191">
                    {log.cost > 0 ? `₹${log.cost.toLocaleString()}` : '-'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-5192">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-5193">
            <div className="text-center" data-id="element-5194">
              <div className="text-gray-600" data-id="element-5195">Total Incidents</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5196">15</div>
              <div className="text-xs text-gray-500" data-id="element-5197">in last week</div>
            </div>
            <div className="text-center" data-id="element-5198">
              <div className="text-gray-600" data-id="element-5199">Total Downtime</div>
              <div className="text-xl font-bold text-red-600" data-id="element-5200">43.8 hrs</div>
              <div className="text-xs text-gray-500" data-id="element-5201">in last week</div>
            </div>
            <div className="text-center" data-id="element-5202">
              <div className="text-gray-600" data-id="element-5203">Avg MTTR</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-5204">2.9 hrs</div>
              <div className="text-xs text-gray-500" data-id="element-5205">in last week</div>
            </div>
            <div className="text-center" data-id="element-5206">
              <div className="text-gray-600" data-id="element-5207">Critical Incidents</div>
              <div className="text-xl font-bold text-red-600" data-id="element-5208">3</div>
              <div className="text-xs text-gray-500" data-id="element-5209">requiring attention</div>
            </div>
            <div className="text-center" data-id="element-5210">
              <div className="text-gray-600" data-id="element-5211">Repair Cost</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5212">₹4.43L</div>
              <div className="text-xs text-gray-500" data-id="element-5213">in last week</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default DowntimeBreakdownAnalysisDashboard;